import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map, fromJS } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import _get from 'lodash/get'
import {
  updateUpstreamContext,
  updateUpstreamContextFromObject,
  CONTEXT_NAME_ADMIN_TITLE,
} from 'services/upstream-context'
import {
  TYPE_CONTENT_PERSON,
} from 'services/content-type'
import { GET_CONTENT } from 'services/content/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from 'services/apollo-graphql'
import {
  CONTENT_IMAGE_LIST,
  getFormattedContentData,
  getOrderedContent,
  mergeTabDataForCW3,
} from 'services/content'
import { useQuery } from '@apollo/client'
import Row, { STYLES } from 'components/Row.v2'
import RowV3 from 'components/Row.v3'
import TileV2Graphql from 'components/Tile.v2/Tile.v2Graphql'
import { EN } from 'services/languages/constants'
import {
  TILE_V2_DISPLAY_ROUND,
  TILE_V2_DISPLAY_DEFAULT,
} from 'components/Tile.v2/constants'
import { get as getConfig } from 'config'
import { TYPE_CONTINUE_WATCHING_V3 } from 'services/pm-section'

const config = getConfig()
const rowV3Active = !!_get(config, ['features', 'rowV3'])

function TileV2GraphqlManager (props) {
  const {
    rowPlacement,
    adminTitle,
    onRenderContent,
    onVisibilityChange,
    sectionIndex,
    titleGroupIds,
    upstreamContext,
    disableHover,
    uiLocation,
    language,
    fetchPolicy,
    authJwt,
    display,
    staticRow,
    touchable,
    asGrid,
    showNumbers,
    sectionType,
    section,
    displayMode,
  } = props

  // Continue watching v3
  const isContinueWatchingV3 = sectionType === TYPE_CONTINUE_WATCHING_V3
  const [removedItems, setRemovedItems] = useState([])
  const [animatingIndex, setAnimatingIndex] = useState(null)

  // query GraphQL for the data
  const { data, loading } = useQuery(GET_CONTENT, {
    variables: {
      language: [language, '_primary'],
      primaryLanguage: [language],
      titleGroupIds,
      imageType: CONTENT_IMAGE_LIST,
      limitedData: true,
      includeSeriesImage: isContinueWatchingV3,
    },
    fetchPolicy,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: authJwt,
    },
  })

  const getRowStyle = () => {
    const { reducedGrid, rowStyle } = props
    const hasProduct = titleGroupIds.some((id) => id.includes('product'))

    if (hasProduct) {
      return STYLES.TILES_PRODUCT
    }

    if (rowStyle) {
      return rowStyle
    }

    if (reducedGrid) {
      return STYLES.S12334
    }

    return STYLES.S12345
  }

  const contentList = _get(data, ['contentList', 'content'], []) || []
  const tabList = section.getIn(['data', 'data', 'tabs', 0, 'tabContent']) || []
  const orderedList = getOrderedContent(titleGroupIds, contentList, 'titleGroupId')
  const finalList = isContinueWatchingV3
    ? mergeTabDataForCW3(orderedList, tabList)
    : orderedList

  // create Immutable data for the underlying components
  const immutableContentList = fromJS(finalList) || List()
  const placeholderContentList = fromJS(titleGroupIds).map((i) => {
    return Map({ titleGroupId: i })
  })

  // Function to remove a slide with animation
  const handleRemoveSlide = (index) => {
    // set animation state
    setAnimatingIndex(index)

    setTimeout(() => {
      setAnimatingIndex(null)
      setRemovedItems([...removedItems, index])
    }, 300)
  }

  const renderItem = (item, params) => {
    const tileData = getFormattedContentData(item)

    // Continue watching v3 remove functionality
    const itemIndex = isContinueWatchingV3 ? item.get('index') : params.index
    const isAnimating = isContinueWatchingV3 ? animatingIndex === itemIndex : false

    return (
      <TileV2Graphql
      key={params.index}
        data={tileData}
        upstreamContext={updateUpstreamContextFromObject(
          upstreamContext,
          {
            contentType: tileData.get('contentType'),
            itemIndex,
            contentId: tileData.get('contentId'),
          },
        )}
        sectionIndex={sectionIndex}
        rowPlacement={rowPlacement}
        adminTitle={adminTitle}
        index={itemIndex}
        uiLocation={uiLocation}
        display={tileData.get('contentType') === TYPE_CONTENT_PERSON ? TILE_V2_DISPLAY_ROUND : display}
        showNumbers={showNumbers}
        displayMode={displayMode}
        isContinueWatchingV3={isContinueWatchingV3}
        onRemove={isContinueWatchingV3 ? () => handleRemoveSlide(itemIndex) : () => {}}
        isAnimating={isAnimating}
      />
    )
  }

  if (rowV3Active) {
    const items = loading
      ? placeholderContentList
      : immutableContentList
        .filter((_item, i) => !removedItems.includes(i))
    return (
      <RowV3
        style={getRowStyle()}
        onRenderContent={onRenderContent}
        onVisibilityChange={(isVisible) => onVisibilityChange(isVisible, items)}
        isContinueWatchingV3={isContinueWatchingV3}
      >
        {items.map((item, index) => renderItem(item, { index }))}
      </RowV3>
    )
  }

  return (
    <Row
      className={touchable ? 'static-metadata' : null}
      items={loading ? placeholderContentList : immutableContentList}
      upstreamContext={updateUpstreamContext(
        upstreamContext,
        CONTEXT_NAME_ADMIN_TITLE,
        adminTitle,
      )}
      createAccessor={() => null}
      style={getRowStyle()}
      onRenderContent={onRenderContent}
      onVisibilityChange={onVisibilityChange}
      rowPlacement={rowPlacement}
      disableScale={true}
      disableHover={disableHover}
      static={staticRow}
      asGrid={asGrid}
    >
      {(item, tileData) => renderItem(item, tileData)}
    </Row>
  )
}

TileV2GraphqlManager.propTypes = {
  titleGroupIds: PropTypes.array.isRequired,
  fetchPolicy: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  upstreamContext: ImmutablePropTypes.map.isRequired,
  adminTitle: PropTypes.string.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  rowPlacement: PropTypes.number.isRequired,
  disableHover: PropTypes.bool,
  reducedGrid: PropTypes.bool,
  onVisibilityChange: PropTypes.func,
  onRenderContent: PropTypes.func,
  rowStyle: PropTypes.string,
  uiLocation: PropTypes.string,
  display: PropTypes.string,
  staticRow: PropTypes.bool,
  touchable: PropTypes.bool,
  asGrid: PropTypes.bool,
  showNumbers: PropTypes.bool,
}

TileV2GraphqlManager.defaultProps = {
  titleGroupIds: [],
  fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  upstreamContext: Map(),
  sectionIndex: 1,
  rowPlacement: 1,
  disableHover: false,
  reducedGrid: false,
  adminTitle: 'Unknown',
  rowStyle: STYLES.S12345,
  language: EN,
  display: TILE_V2_DISPLAY_DEFAULT,
  staticRow: false,
  asGrid: false,
  showNumbers: false,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        authJwt: state.auth.get('jwt'),
        touchable: state.app.getIn(['viewport', 'touchable'], false),
        auth: state.auth,
      }
    },
  ),
)(TileV2GraphqlManager)
